import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { BodyReliefHeroSection } from '../components/BodyReliefHeroSection';
import { BodyReliefProblemSection } from '../components/BodyReliefProblemSection';
import { BodyReliefSolutionSection } from '../components/BodyReliefSolutionSection';
import { BodyReliefMechanismSection } from '../components/BodyReliefMechanismSection';
import { BodyReliefBenefitsSection } from '../components/BodyReliefBenefitsSection';
import { BodyReliefUseCasesSection } from '../components/BodyReliefUseCasesSection';
import { HowToUseSection } from '../components/HowToUseSection';
import { BodyReliefTrustSection } from '../components/BodyReliefTrustSection';
import { BundleSection } from '../components/BundleSection';
import { FAQSection } from '../components/FAQSection';
import { BodyReliefCTASection } from '../components/BodyReliefCTASection';
import { PolicyModal } from '../components/PolicyModal';
import { SalesNotification } from '../components/SalesNotification';
type PolicyType = 'privacy' | 'terms' | 'refund';
const navigationLinks = [{
  name: 'How It Works',
  href: '#how-it-works'
}, {
  name: 'Benefits',
  href: '#benefits'
}, {
  name: 'How To Use',
  href: '#how-to-use'
}, {
  name: 'Pricing',
  href: '#bundles'
}, {
  name: 'FAQ',
  href: '#faq'
}];
export function GinextBodyReliefPage() {
  const [policyModal, setPolicyModal] = useState<{
    isOpen: boolean;
    type: PolicyType;
  }>({
    isOpen: false,
    type: 'privacy'
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  // Handle scrolling to hash on mount and when hash changes
  useEffect(() => {
    if (location.hash) {
      setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          const yOffset = -80;
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({
            top: y,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [location.hash]);
  const openPolicy = (type: PolicyType) => {
    setPolicyModal({
      isOpen: true,
      type
    });
  };
  const closePolicy = () => {
    setPolicyModal({
      ...policyModal,
      isOpen: false
    });
  };
  const handleNavClick = (href: string) => {
    setMobileMenuOpen(false);
    setTimeout(() => {
      const element = document.querySelector(href);
      if (element) {
        const yOffset = -80;
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({
          top: y,
          behavior: 'smooth'
        });
      }
    }, 100);
  };
  return <main className="w-full min-h-screen bg-bone overflow-x-hidden" data-id="element-1576">
      {/* Header */}
      <header className="bg-white md:bg-white/95 md:backdrop-blur-sm border-b border-bone-dark fixed top-0 left-0 right-0 z-50 shadow-sm" data-id="element-1577">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-1578">
          <Link to="/" className="flex items-center" data-id="element-1579">
            <img src="https://cdn.magicpatterns.com/uploads/483EwVYaDBT86YSQWmyKfo/GINEXT_LOGO.png" alt="GINEXT" className="h-6 w-auto" loading="eager" data-id="element-1580" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6" data-id="element-1581">
            {navigationLinks.map(link => <a key={link.name} href={link.href} onClick={e => {
            e.preventDefault();
            handleNavClick(link.href);
          }} className="text-sm font-light text-charcoal hover:text-ginger transition-colors cursor-pointer" data-id="element-1582">
                {link.name}
              </a>)}
          </nav>

          {/* Mobile Menu Button */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-charcoal hover:text-ginger transition-colors" aria-label="Toggle menu" data-id="element-1583">
            {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-1584" /> : <Menu className="w-6 h-6" data-id="element-1585" />}
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        <AnimatePresence data-id="element-1586">
          {mobileMenuOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} className="md:hidden overflow-hidden border-t border-bone-dark bg-white" data-id="element-1587">
              <nav className="px-4 py-4 space-y-1" data-id="element-1588">
                {navigationLinks.map(link => <motion.div key={link.name} data-id="element-1589">
                    <a href={link.href} onClick={e => {
                e.preventDefault();
                setMobileMenuOpen(false);
                handleNavClick(link.href);
              }} className="block py-3 px-4 text-base font-light text-charcoal hover:text-ginger hover:bg-bone/50 rounded-lg transition-all cursor-pointer" data-id="element-1590">
                      {link.name}
                    </a>
                  </motion.div>)}
              </nav>
            </motion.div>}
        </AnimatePresence>
      </header>

      {/* Add padding to account for fixed header */}
      <div className="pt-[57px]" data-id="element-1591">
        <BodyReliefHeroSection data-id="element-1592" />
        <BodyReliefProblemSection data-id="element-1593" />
        <BodyReliefSolutionSection data-id="element-1594" />
        <div id="how-it-works" className="scroll-mt-20" data-id="element-1595">
          <BodyReliefMechanismSection data-id="element-1596" />
        </div>
        <div id="benefits" className="scroll-mt-20" data-id="element-1597">
          <BodyReliefBenefitsSection data-id="element-1598" />
        </div>
        <BodyReliefUseCasesSection data-id="element-1599" />
        <div id="how-to-use" className="scroll-mt-20" data-id="element-1600">
          <HowToUseSection data-id="element-1601" />
        </div>
        <BodyReliefTrustSection data-id="element-1602" />
        <div id="bundles" className="scroll-mt-20" data-id="element-1603">
          <BundleSection data-id="element-1604" />
        </div>
        <div id="faq" className="scroll-mt-20" data-id="element-1605">
          <FAQSection data-id="element-1606" />
        </div>
        <BodyReliefCTASection data-id="element-1607" />
      </div>

      {/* Footer */}
      <footer className="bg-charcoal text-gray-400 py-4 text-center text-xs border-t border-white/10" data-id="element-1608">
        <div className="max-w-6xl mx-auto px-4" data-id="element-1609">
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-3" data-id="element-1610">
            <button onClick={() => openPolicy('privacy')} className="hover:text-ginger transition-colors underline" data-id="element-1611">
              Privacy Policy
            </button>
            <button onClick={() => openPolicy('terms')} className="hover:text-ginger transition-colors underline" data-id="element-1612">
              Terms of Service
            </button>
            <button onClick={() => openPolicy('refund')} className="hover:text-ginger transition-colors underline" data-id="element-1613">
              Refund Policy
            </button>
          </div>
          <p data-id="element-1614">© {new Date().getFullYear()} GINEXT. All rights reserved.</p>
          <p className="mt-1" data-id="element-1615">
            These statements have not been evaluated by the Food and Drug
            Administration. This product is not intended to diagnose, treat,
            cure, or prevent any disease.
          </p>
        </div>
      </footer>

      <PolicyModal isOpen={policyModal.isOpen} onClose={closePolicy} type={policyModal.type} data-id="element-1616" />

      {/* Sales Notification */}
      <SalesNotification data-id="element-1617" />
    </main>;
}